import { type CSSProperties, type ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { BrandLogo, ThemeToggle } from '../components/ui/index.js';
import { useDirection, setLocale } from '../hooks/useDirection.js';
import { getBrand } from '../lib/brandClient.js';
import { assetUrl } from '../lib/publicApi.js';
import { useSiteContextStore } from '../lib/siteContextStore.js';
import { deriveStoreTheme } from '../lib/storeTheme.js';

/**
 * Split-screen auth shell (login / signup) — brand-side marketing panel on the
 * left, form-side on the right. Mirrors the IQ ERP design (prototype/Login.html).
 * The brand-side collapses below `md`, leaving just the centered form.
 */
export function AuthLayout({ children }: { children: ReactNode }): JSX.Element {
  const dir = useDirection();
  const { t, i18n } = useTranslation();
  const isAr = i18n.language.startsWith('ar');
  const brand = getBrand();

  // On a tenant's `app` custom domain, brand the auth shell with that tenant's
  // name / logo / colours (host-resolved by SiteContextGate). On the platform
  // host this is null → the default product branding, unchanged.
  const appTenant = useSiteContextStore((s) => (s.kind === 'app' ? s.tenant : null));
  const displayName = appTenant?.name ?? brand.name.full;
  // The tenant's own tagline (Store settings) when on their app domain; else the
  // generic product tagline.
  const tagline = appTenant?.tagline?.trim() || t('app.tagline', { defaultValue: 'Finance & Retail Suite' });
  const tenantLogo = appTenant ? assetUrl(appTenant.logoUrl) : null;
  // Browser TAB title on the login screen = the store's own name, not "ERP".
  useEffect(() => { if (displayName) document.title = displayName; }, [displayName]);
  const themeStyle = appTenant?.theme
    ? (deriveStoreTheme(appTenant.theme.themeColor, appTenant.theme.secondaryColor) as CSSProperties)
    : undefined;

  return (
    <div className="erp-auth" dir={dir} style={themeStyle}>
      <div className="erp-auth-ctl">
        <div className="seg" role="group" aria-label={t('actions.switchLanguage')}>
          <button type="button" aria-pressed={!isAr} onClick={() => void setLocale('en')}>EN</button>
          <button type="button" aria-pressed={isAr} onClick={() => void setLocale('ar')}>ع</button>
        </div>
        <ThemeToggle />
      </div>

      {/* Brand / marketing side */}
      <section className="erp-auth-brand">
        <span className="erp-auth-blob" style={{ width: 280, height: 280, background: 'var(--primary)', top: '28%', insetInlineStart: -80 }} />
        <span className="erp-auth-blob" style={{ width: 200, height: 200, background: 'var(--accent)', bottom: '8%', insetInlineStart: '32%' }} />

        <div className="erp-auth-lockup">
          <span className="mk">
            {tenantLogo ? (
              <img src={tenantLogo} alt="" className="h-full w-full rounded-xl object-cover" referrerPolicy="no-referrer" />
            ) : (
              <BrandLogo variant="mark" inverse />
            )}
          </span>
          <span>
            <div className="nm">{displayName}</div>
            <div className="sub">{tagline}</div>
          </span>
        </div>

        <div className="erp-auth-hero">
          <h1>{t('auth:hero.title', { defaultValue: 'Run your whole business in one place.' })}</h1>
          <p>
            {t('auth:hero.subtitle', {
              defaultValue:
                'POS, inventory, accounting, payroll and reports — bilingual, multi-currency, and ZATCA-ready. Built for the Gulf.',
            })}
          </p>
        </div>

        <div className="erp-auth-stats">
          <div>
            <div className="v">12</div>
            <div className="k">{t('auth:hero.branches', { defaultValue: 'Branches' })}</div>
          </div>
          <div>
            <div className="v">5</div>
            <div className="k">{t('auth:hero.currencies', { defaultValue: 'Currencies' })}</div>
          </div>
          <div>
            <div className="v">99.9%</div>
            <div className="k">{t('auth:hero.uptime', { defaultValue: 'Uptime' })}</div>
          </div>
        </div>
      </section>

      {/* Form side */}
      <section className="erp-auth-form">{children}</section>
    </div>
  );
}
